import React from "react";

interface BulletPoint {
  point: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  bullets?: string | BulletPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultBullets = [
  {
    point: "Light Absorption",
    description: "Chlorophyll molecules capture sunlight energy",
    icon: "☀️",
  },
  {
    point: "Water Splitting",
    description: "H2O molecules split into H+ and O2",
    icon: "💧",
  },
  {
    point: "Electron Transport",
    description: "Electrons flow through photosystems",
    icon: "⚡",
  },
  {
    point: "ATP Production",
    description: "Chemical energy is stored as ATP",
    icon: "🔋",
  },
  {
    point: "Carbon Fixation",
    description: "CO2 is converted to glucose",
    icon: "🌱",
  },
  {
    point: "Oxygen Release",
    description: "O2 is released as a byproduct",
    icon: "🌬️",
  },
];

const BulletSlide2 = {
  id: "bullet-slide2",
  name: "Bullet Points (Timeline)",
  category: "Bullet Points",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Key Stages of Photosynthesis",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "Understanding the Process Step by Step",
    },
    {
      id: "bullets",
      label: "Bullets (JSON array of { point, description, icon })",
      type: "text",
      defaultValue: JSON.stringify(defaultBullets, null, 2),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #14b8a6 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "Key Stages of Photosynthesis",
      subtitle = "Understanding the Process Step by Step",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #14b8a6 100%)",
      bullets = JSON.stringify(defaultBullets),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleBulletEdit = (index: number, field: string, value: string) => {
      let parsedBullets = defaultBullets;
      try {
        if (typeof bullets === "string") {
          parsedBullets = JSON.parse(bullets);
        }
      } catch (error) {
        console.warn("Error parsing bullets:", error);
      }

      const updatedBullets = [...parsedBullets];
      updatedBullets[index] = {
        ...updatedBullets[index],
        [field]: value,
      };

      handleEdit("bullets")(JSON.stringify(updatedBullets));
    };

    let parsedBullets = defaultBullets;
    try {
      if (typeof bullets === "string") {
        const parsed = JSON.parse(bullets);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedBullets = parsed;
        }
      } else if (Array.isArray(bullets) && bullets.length > 0) {
        parsedBullets = bullets as BulletPoint[];
      }
    } catch (error) {
      console.warn("Error parsing bullets, using defaults:", error);
    }

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 p-8 text-center">
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("title")(e.currentTarget.textContent || "")
            }
            className="text-4xl font-bold mb-2 text-teal-100"
          >
            {title}
          </div>
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("subtitle")(e.currentTarget.textContent || "")
            }
            className="text-xl font-light text-teal-100/90"
          >
            {subtitle}
          </div>
        </div>

        {/* Timeline Content */}
        <div className="absolute inset-0 mt-32 mb-16 px-8">
          <div className="relative h-full flex flex-col justify-center">
            {/* Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-teal-500/20 transform -translate-x-1/2" />

            {/* Bullet Points */}
            <div className="space-y-12">
              {parsedBullets.map((bullet, idx) => (
                <div
                  key={idx}
                  className={`relative flex items-center ${
                    idx % 2 === 0 ? "flex-row" : "flex-row-reverse"
                  }`}
                >
                  {/* Content */}
                  <div
                    className={`w-[calc(50%-3rem)] ${
                      idx % 2 === 0 ? "pr-12 text-right" : "pl-12 text-left"
                    }`}
                  >
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) =>
                        handleBulletEdit(
                          idx,
                          "point",
                          e.currentTarget.textContent || "",
                        )
                      }
                      className="text-2xl font-bold text-teal-100 mb-3"
                    >
                      {bullet.point}
                    </div>
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) =>
                        handleBulletEdit(
                          idx,
                          "description",
                          e.currentTarget.textContent || "",
                        )
                      }
                      className="text-base text-teal-100/80 leading-relaxed max-h-[80px] overflow-y-auto
                                scrollbar-thin scrollbar-track-teal-900/20 scrollbar-thumb-teal-600/50"
                    >
                      {bullet.description}
                    </div>
                  </div>

                  {/* Timeline Node */}
                  <div
                    className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 
                                bg-teal-600/80 rounded-full flex items-center justify-center 
                                text-2xl border-4 border-teal-400/30"
                  >
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) =>
                        handleBulletEdit(
                          idx,
                          "icon",
                          e.currentTarget.textContent || "",
                        )
                      }
                      className="w-full h-full flex items-center justify-center"
                    >
                      {bullet.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default BulletSlide2;
